import type { XLMModel, XLMNode, XLMValue } from "../api/e2e";

export const XLM_FIGURE_COLOR_HEX = {
  formula: "#000000",
  historicalInput: "#0000FF",
  forecastInput: "#FF0000",
  derivedPercent: "#008000",
  reference: "#7030A0",
} as const;

export type XLMFigureColorRole =
  | "formula"
  | "historical-input"
  | "forecast-input"
  | "derived-percent"
  | "reference";

export const XLM_FIGURE_COLOR_BY_ROLE: Record<XLMFigureColorRole, string> = {
  formula: XLM_FIGURE_COLOR_HEX.formula,
  "historical-input": XLM_FIGURE_COLOR_HEX.historicalInput,
  "forecast-input": XLM_FIGURE_COLOR_HEX.forecastInput,
  "derived-percent": XLM_FIGURE_COLOR_HEX.derivedPercent,
  reference: XLM_FIGURE_COLOR_HEX.reference,
};

export const XLM_FIGURE_COLOR_CSS_BY_ROLE: Record<XLMFigureColorRole, string> =
  {
    formula: "var(--statement-figure-formula)",
    "historical-input": "var(--statement-figure-historical-input)",
    "forecast-input": "var(--statement-figure-forecast-input)",
    "derived-percent": "var(--statement-figure-derived-percent)",
    reference: "var(--statement-figure-reference)",
  };

export interface XLMFigureColorModelContext {
  renderedIdentifiers: ReadonlySet<string>;
  renderedPeriods: ReadonlySet<string>;
}

interface XLMFigureColorOptions {
  forecast: boolean;
  formulaOnlyPercentRow: boolean;
  model: XLMFigureColorModelContext;
}

const DIRECT_MODEL_REFERENCE =
  /^=(?<identifier>[0-9]*[A-Za-z_][A-Za-z0-9_]*)(?:\s*\[(?<period>\d{4}(?:Q\d|H\d)?)\])?$/u;
const DIRECT_EXCEL_REFERENCE =
  /^=(?:'[^']+'|[A-Za-z0-9_.]+!)?\$?[A-Za-z]{1,3}\$?\d{1,7}$/u;

function addNodeIdentifiers(
  identifiers: Set<string>,
  nodes: readonly XLMNode[],
): void {
  for (const node of nodes) {
    identifiers.add(node.identifier);
    addNodeIdentifiers(identifiers, node.breakdown?.items ?? []);
  }
}

export function xlmFigureColorContext(
  model: XLMModel,
): XLMFigureColorModelContext {
  const renderedIdentifiers = new Set<string>();
  addNodeIdentifiers(renderedIdentifiers, model.nodes);
  return {
    renderedIdentifiers,
    renderedPeriods: new Set([
      ...model.fiscal_years.map((period) => String(period.year)),
      ...model.quarters.map((period) => `${period.year}Q${period.quarter}`),
      ...model.half_years.map((period) => `${period.year}H${period.half}`),
    ]),
  };
}

function isDirectReference(
  formula: string,
  model: XLMFigureColorModelContext,
): boolean {
  const modelReference = DIRECT_MODEL_REFERENCE.exec(formula);
  const identifier = modelReference?.groups?.identifier;
  if (identifier && model.renderedIdentifiers.has(identifier)) {
    const period = modelReference.groups?.period;
    return period === undefined || model.renderedPeriods.has(period);
  }
  return DIRECT_EXCEL_REFERENCE.test(formula);
}

export function xlmFigureColorRole(
  value: XLMValue | undefined,
  options: XLMFigureColorOptions,
): XLMFigureColorRole | null {
  if (!value) return null;
  if (value.formula === null || value.formula === undefined) {
    if (typeof value.value !== "number") return null;
    return options.forecast ? "forecast-input" : "historical-input";
  }
  if (options.formulaOnlyPercentRow) return "derived-percent";
  if (isDirectReference(value.formula, options.model)) return "reference";
  return "formula";
}
